import { useEffect, useState } from "react";
import { styled } from "styled-components";
import {
  Layout,
  LinkText,
  useContactContext,
  useSignupContext,
} from "Components";
import { Body1, H1, H2, H4 } from "melodies-source/Text";
import { TextInput } from "melodies-source/TextInput";
import { Button } from "melodies-source/Button";
import { ReactComponent as EmailIcon } from "assets/svg/email-outlined.svg";
import { ReactComponent as ArrowRightIcon } from "assets/svg/arrow-right.svg";
import { ReactComponent as SvgSetLogo } from "assets/svg/logo.svg";
import { getLoginUrl, useUser } from "auth";
import { useMobileMediaQuery } from "hooks/useMobileMediaQuery";
import { CardContent } from "Components/Layout/Content/Card";

const EMAIL_REGEX = /^(.+)@(.+)$/;

export const Email = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const { handleStart, handleNext, assetUrl } = useSignupContext();
  const [email, setEmail] = useState<string>("");
  const [emailIsValid, setEmailIsValid] = useState(true);
  const { setShowContact } = useContactContext();
  const isMobile = useMobileMediaQuery();
  const Heading = isMobile ? H2 : H1;
  const { user, logout } = useUser();

  const validateEmail = () => {
    const valid = EMAIL_REGEX.test(email);
    if (valid) {
      setLoading(true);
      handleStart({ email }).finally(() => setLoading(false));
    } else {
      setEmailIsValid(false);
    }
  };

  useEffect(() => {
    if (!emailIsValid) setEmailIsValid(true);
  }, [email]);

  return (
    <StyledLayout
      hideAppBar
      cardProps={{
        disableCardHeader: true,
        cardLogo: <SetLogo />,
        size: "large",
      }}
      belowContent={
        isMobile ? undefined : (
          <FanMessage>
            If you are a fan trying to check in,{" "}
            <a href="https://set.live" rel="noopener noreferrer">
              Click Here
            </a>
          </FanMessage>
        )
      }
    >
      <EmailContainer>
        <SETPreview
          src={assetUrl({
            url: "https://storage.googleapis.com/set-live.appspot.com/set-live/signup/set-apps-preview.png",
          })}
        />
        <TextContainer>
          <Heading>Connect With Your Fans Everywhere</Heading>
          <Body1>
            Whether on social media or at shows, our SET suite of tools lets
            artists get to know their fans.
          </Body1>
        </TextContainer>
        {user.isAnonymous ? (
          <Actions>
            <TextInput
              name="email"
              value={email}
              onChange={(email) => setEmail(email.trim())}
              label="Which email will you use for this account?"
              placeholder="Please enter your email"
              leftIcon={<EmailIcon />}
              {...(!emailIsValid && {
                hasError: true,
                helperText: "Please enter a valid email",
              })}
            />
            <Button
              disabled={loading}
              onClick={validateEmail}
              rightIcon={<ArrowRightIcon />}
              loading={loading}
            >
              {isMobile
                ? "Create Your Artist Account"
                : "Create Your Free Account"}
            </Button>
            <Body1>
              Already have an account? <a href={getLoginUrl()}>Log in</a>
            </Body1>
          </Actions>
        ) : (
          <Actions>
            <H4>{user.email}</H4>
            <Button onClick={() => handleNext()}>Continue</Button>
            <Body1>
              Want to use a different email?{" "}
              <LinkText onClick={() => logout(true)}>Change email</LinkText>
            </Body1>
          </Actions>
        )}
        {isMobile && (
          <MobileFanButton
            variant="outlined"
            onClick={() => {
              window.location.href = "https://set.live";
            }}
          >
            Fans, Click Here to Check In
          </MobileFanButton>
        )}
      </EmailContainer>
    </StyledLayout>
  );
};

const StyledLayout = styled(Layout)`
  ${({ theme }) => theme.media.mobile} {
    ${CardContent} {
      padding-bottom: 0;
    }
  }
`;

const EmailContainer = styled.div`
  display: grid;
  grid-template-areas: "image" "text" "actions";
  justify-items: center;
  gap: 20px;
  text-align: center;
  margin: 16px auto 32px;

  ${Body1} a {
    font-weight: 600;
  }

  ${({ theme }) => theme.media.maxTablet} {
    margin: 32px auto;
  }

  ${({ theme }) => theme.media.mobile} {
    grid-template-areas: "text" "actions" "fanAction" "image";
    margin: 0 auto;
    overflow: hidden;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: text;
  gap: 4px;

  ${H1} {
    color: #1b0076;
    font-weight: 700;
    font-size: 38px;
    line-height: 45px;
  }

  ${H2} {
    color: #1b0076;
    font-weight: 700;
    line-height: 30px;
  }

  ${Body1} {
    font-weight: 300;
  }

  ${({ theme }) => theme.media.maxTablet} {
    gap: 2px;
    ${H1} {
      font-size: 28px;
      line-height: 38px;
    }
    ${Body1} {
      font-size: 12px;
      line-height: 18px;
    }
  }

  ${({ theme }) => theme.media.mobile} {
    width: 100%;
    max-width: 310px;
    ${Body1} {
      display: none;
    }
  }
`;

const FanMessage = styled(Body1)`
  color: #fff;
  width: 100%;
  text-align: center;
  margin-top: 16px;

  a {
    color: #fff;
    font-weight: 600;
    text-decoration: underline;
  }
`;

const MobileFanButton = styled(Button)`
  grid-area: fanAction;
  margin-top: 60px;
  margin-bottom: 20px;
  width: 100%;
  max-width: 310px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 390px;
  gap: 12px;
  grid-area: actions;
  margin-top: 12px;

  ${({ theme }) => theme.media.mobile} {
    gap: 10px;
    margin-top: 0;
    max-width: 310px;
  }
`;

const SETPreview = styled.img`
  width: 451px;
  height: 225px;
  grid-area: image;

  ${({ theme }) => theme.media.mobile} {
    width: 100%;
    height: auto;
    margin-bottom: -1px;
  }
`;

const SetLogo = styled(SvgSetLogo)`
  position: absolute;
  top: 24px;
  left: 24px;
  width: 86px;
  height: 44px;

  ${({ theme }) => theme.media.mobile} {
    align-self: center;
    position: relative;
    width: 91px;
    height: 46px;
    margin-top: 32px;
    margin-bottom: 12px;
    top: initial;
    left: initial;
  }
`;
